import { ReactElement, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';

import { ICustomDictionaryItem, ICustomDictionaryType } from '~/services/api/dictionaries/types';
import Dictionaries from './Dictionaries';

import DictionaryActionTYPE from './enums/dictionaryActionTypes';
import { getAddLabel, getEditLabel, isRequiredCustomField } from './helpers/dictionaryType';
import useDictionaryItemAction, { IDictionaryItemAction } from './hooks/useDictionaryItemAction';

interface IProps {
  defaultDictionaryType?: ICustomDictionaryType;
  hideHeader?: boolean;
  itemAction?: IDictionaryItemAction;
}

const ClinicDictionaries = ({
  defaultDictionaryType,
  hideHeader,
  itemAction,
}: IProps): ReactElement => {
  const { id: clinicId } = useParams<{ id: string }>();
  // dictionaryType -> selected dictionaryType, default value set in onMount function (first element of dictionariesTypes)
  // dictionaryItem -> selected dictionaryItem
  const [dictionaryType, setDictionaryType] =
    useState<ICustomDictionaryType>(defaultDictionaryType);

  const ownItemAction = useDictionaryItemAction();
  const {
    actionType,
    dictionaryItem,
    setDictionaryItem,
    onAddDictionaryItem,
    onEditDictionaryItem,
    onClose,
  } = itemAction || ownItemAction;

  const { onArchiveTypeItem, onRestoreTypeItem, onUpdateTypeItem, onAddTypeItem } = useStoreActions(
    actions => actions.dictionaries,
  );
  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);

  const hasCustomFields = useMemo(() => !!dictionaryType.customFields, [dictionaryType]);

  const hasAlternativeValues = useMemo(() => !!dictionaryType.alternativeValues, [dictionaryType]);

  const handleDictionaryItemUpdateOrAdd = async (updatedItem: ICustomDictionaryItem) => {
    if (!updatedItem?.value) {
      showError('Value can not be blank');
      return;
    }
    if (
      hasCustomFields &&
      !updatedItem?.abbreviation &&
      dictionaryType.customFields.some(isRequiredCustomField)
    ) {
      showError('Abbreviation can not be blank');
      return;
    }

    const method = actionType === DictionaryActionTYPE.add ? onAddTypeItem : onUpdateTypeItem;
    const word = actionType === DictionaryActionTYPE.add ? 'created' : 'updated';

    try {
      await method({ ...updatedItem, type: dictionaryType.type, clinicId: Number(clinicId) });
      onClose();
      showNotify({ message: `Dictionary successfully ${word}` });
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const confirmText = dictionaryItem?.isArchived ? 'Restore' : 'Archive';
  const confirmMethod = dictionaryItem?.isArchived ? onRestoreTypeItem : onArchiveTypeItem;
  const description = dictionaryItem?.isArchived
    ? `Are you sure you want to restore the dictionary ${dictionaryItem?.value}?`
    : `Are you sure you want to archive the dictionary ${dictionaryItem?.value}? You will be able to restore it any time.`;

  const isAdding = actionType === DictionaryActionTYPE.add;
  const actionModalText = isAdding ? getAddLabel(dictionaryType) : 'Save';
  const modalTitle = isAdding ? getAddLabel(dictionaryType) : getEditLabel(dictionaryType);

  const onConfirmation = async (): Promise<void> => {
    try {
      await confirmMethod({
        type: dictionaryType?.type,
        id: dictionaryItem.id,
        clinicId: Number(clinicId),
      });
    } catch (error) {
      showError(extractErrorMessage(error));
    }
    setDictionaryItem(null);
  };

  return (
    <Dictionaries
      dictionaryType={dictionaryType}
      defaultDictionaryType={defaultDictionaryType}
      dictionaryTypes={[]}
      hasCustomFields={hasCustomFields}
      actionType={actionType}
      dictionaryItem={dictionaryItem}
      hasAlternativeValues={hasAlternativeValues}
      actionModalText={actionModalText}
      modalTitle={modalTitle}
      confirmText={confirmText}
      description={description}
      clinicId={Number(clinicId)}
      hideHeader={hideHeader}
      onAddDictionaryItem={onAddDictionaryItem}
      setDictionaryItem={setDictionaryItem}
      setDictionaryType={setDictionaryType}
      onEditDictionaryItem={onEditDictionaryItem}
      handleDictionaryItemUpdateOrAdd={handleDictionaryItemUpdateOrAdd}
      onClose={onClose}
      onConfirmation={onConfirmation}
    />
  );
};

export default ClinicDictionaries;
